import GridCard from '@Guidelines/Component/GridCard';
import { theme } from '@Guidelines/Theme/theme';
import type AuditEntryView from '@UI/Store/AuditTrail/Views/AuditEntryView';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Tag } from '@verclam/ui-kit';

// A single module-level formatter: `Intl.DateTimeFormat` construction is costly, and the instance
// is stateless and reusable across every call.
const timestampFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'medium' });

const AUDIT_ENTRY_CARD_TRACK_COUNT = 4;
const AUDIT_ENTRY_CARD_ROW_GAP = '0.625rem';

const headerStyle = css`
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.75rem;
`;

const timestampStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const fieldStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
`;

const fieldLabelStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const fieldValueStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

/**
 * Stacks the optional fields at the same gap the card's other tracks use, so grouping them into one
 * track reads as if they were still spaced individually. Renders empty (zero height) when none of the
 * five conditions below hold — the subgrid still reserves its track, the same trade-off `FacilityCard`
 * accepts for its own optional-details track.
 */
const optionalFieldsStyle = css`
	display: flex;
	flex-direction: column;
	gap: ${AUDIT_ENTRY_CARD_ROW_GAP};
`;

type Props = {
	readonly entry: AuditEntryView;
	/** Resolved from `usersStore` by the caller, never `undefined` — `Utilisateur #42` (or a
	 * dedicated label when the act carries no actor at all) is the fallback, not a blank field. */
	readonly actorLabel: string;
	/** Resolved from `accountFacilitiesStore` by the caller; `null` when the act carries no facility. */
	readonly facilityLabel: string | null;
};

/**
 * One audit entry, read-only: no action of any kind is rendered on this card, at any permission
 * level — the screen offers exactly one action, "Exporter", and it lives on the page header, not
 * here. The resource line always shows an identifier even when `resourceType` is `null`
 * (authentication entries carry no resource type by construction) or `resourceId` is absent.
 *
 * Laid out as a four-track subgrid: header (timestamp + outcome tag), the "Action" field, the
 * "Acteur" field, and a block grouping every optional field (Ressource, Dossier, Agence, Adresse
 * source, Contexte). "Action" and "Acteur" carry their own track — every entry, whatever its
 * outcome, always has both — so they keep settling on the same row as the equivalent field on a
 * neighboring card even when one of them wraps to a second line. The five optional fields, by
 * contrast, are each absent on most entries; giving each its own track would reserve up to five
 * blank rows per card, so they are grouped into the one block a track can afford to spend on them.
 */
const AuditEntryCard: React.FC<Props> = ({ entry, actorLabel, facilityLabel }) => {
	const resourceLabel =
		entry.resourceType !== null
			? `${entry.resourceType.label}${entry.resourceId !== null ? ` #${entry.resourceId}` : ''}`
			: null;

	return (
		<GridCard trackCount={AUDIT_ENTRY_CARD_TRACK_COUNT} rowGap={AUDIT_ENTRY_CARD_ROW_GAP}>
			<div css={headerStyle}>
				<p css={timestampStyle}>{timestampFormatter.format(new Date(entry.occurredAt))}</p>
				<Tag
					value={entry.outcome.label}
					severity={entry.outcome.value === 'success' ? 'success' : 'danger'}
					rounded
				/>
			</div>
			<div css={fieldStyle}>
				<span css={fieldLabelStyle}>Acteur</span>
				<p css={fieldValueStyle}>{actorLabel}</p>
			</div>
			<div css={fieldStyle}>
				<span css={fieldLabelStyle}>Action</span>
				<p css={fieldValueStyle}>{entry.action.label}</p>
			</div>

			<div css={optionalFieldsStyle}>
				{resourceLabel !== null && (
					<div css={fieldStyle}>
						<span css={fieldLabelStyle}>Ressource</span>
						<p css={fieldValueStyle}>{resourceLabel}</p>
					</div>
				)}
				{entry.context !== null && (
					<div css={fieldStyle}>
						<span css={fieldLabelStyle}>Contexte</span>
						<p css={fieldValueStyle}>{entry.context}</p>
					</div>
				)}
				{entry.beneficiaryId !== null && (
					<div css={fieldStyle}>
						<span css={fieldLabelStyle}>Dossier</span>
						<p css={fieldValueStyle}>{`Dossier #${entry.beneficiaryId}`}</p>
					</div>
				)}
				{facilityLabel !== null && (
					<div css={fieldStyle}>
						<span css={fieldLabelStyle}>Agence</span>
						<p css={fieldValueStyle}>{facilityLabel}</p>
					</div>
				)}

				{entry.ipAddress !== null && (
					<div css={fieldStyle}>
						<span css={fieldLabelStyle}>Adresse source</span>
						<p css={fieldValueStyle}>{entry.ipAddress}</p>
					</div>
				)}
			</div>
		</GridCard>
	);
};

export default AuditEntryCard;
